/**
 * `v-press`: native press feedback for anything tappable (rows, cards, icon buttons, pills).
 *
 * CSS `:active` alone flashes while you scroll a list and never shows on a quick iOS tap. This mirrors UIKit:
 * the pressed state waits 60ms inside a scroller (a scroll that starts in that window never highlights),
 * cancels when the finger moves more than 8px or anything scrolls, and releases with the spring in motion.css.
 * A quick tap still gets a short visible dip.
 *
 *   <button v-press>                      card (scale .97)
 *   <div v-press.row>                     iOS list row (dims, no scale)
 *   <button v-press.icon> / v-press.pill  scale .92 / .96
 *   <button v-press.pill.haptic>          + haptic('selection') on activation
 *   <button v-press:toggle.pill>          + haptic('toggle') on activation (arg = intent)
 *   <div v-press="{ variant: 'row', disabled: busy }">   options; `v-press="false"` turns it off
 *
 * Haptics fire on click (activation), never on touch-down. Registered globally (main.ts and the dev entries).
 */
import type { App, Directive, DirectiveBinding } from 'vue'
import { haptic, type HapticIntent } from '@/native/haptics'

export type PressVariant = 'row' | 'card' | 'icon' | 'pill'
export interface PressOptions {
    variant?: PressVariant
    /** `true` = 'selection'. */
    haptic?: HapticIntent | boolean
    disabled?: boolean
}
export type PressValue = PressVariant | PressOptions | boolean | null | undefined

/** Wait this long before showing the press inside a scroller. */
export const PRESS_SCROLL_DELAY_MS = 60
/** Moving further than this cancels the press. */
export const PRESS_SLOP_PX = 8
/** A tap shorter than the scroll delay still shows the press this long. */
const TAP_FLASH_MS = 90

const VARIANTS: PressVariant[] = ['row', 'card', 'icon', 'pill']

interface PressConfig {
    variant: PressVariant
    haptic: HapticIntent | null
    disabled: boolean
}

interface PressState {
    config: PressConfig
    pressed: boolean
    timer: ReturnType<typeof setTimeout> | null
    flash: ReturnType<typeof setTimeout> | null
    pointerId: number | null
    x: number
    y: number
    cleanup: (() => void) | null
    onDown: (e: PointerEvent) => void
    onClick: () => void
}

const states = new WeakMap<HTMLElement, PressState>()

function resolveConfig(binding: DirectiveBinding<PressValue>): PressConfig {
    const v = binding.value
    const opts: PressOptions = typeof v === 'string' ? { variant: v } : v && typeof v === 'object' ? v : {}
    const modVariant = VARIANTS.find((name) => binding.modifiers[name])
    const intent: HapticIntent | null =
        typeof opts.haptic === 'string' ? opts.haptic
        : opts.haptic === true ? 'selection'
        : binding.arg ? (binding.arg as HapticIntent)
        : binding.modifiers.haptic ? 'selection'
        : null
    return {
        variant: opts.variant ?? modVariant ?? 'card',
        haptic: intent,
        disabled: v === false || Boolean(opts.disabled),
    }
}

function isDisabled(el: HTMLElement, s: PressState): boolean {
    return s.config.disabled || (el as HTMLButtonElement).disabled === true || el.getAttribute('aria-disabled') === 'true'
}

/** Inside something that scrolls (where a touch may become a scroll)? Checked on touch-down only. */
function inScroller(el: HTMLElement): boolean {
    for (let n = el.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
        const cs = getComputedStyle(n)
        const y = /(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1
        const x = /(auto|scroll)/.test(cs.overflowX) && n.scrollWidth > n.clientWidth + 1
        if (x || y) return true
    }
    return false
}

/** (Re)apply our classes. Vue rewrites className when an element's bound class changes, so `updated` calls this too. */
function applyClasses(el: HTMLElement, s: PressState) {
    el.classList.add('lh-press', 'lh-press-js')
    for (const name of VARIANTS) el.classList.toggle(`lh-press--${name}`, name === s.config.variant)
    el.classList.toggle('is-pressed', s.pressed)
}

function setPressed(el: HTMLElement, s: PressState, on: boolean) {
    s.pressed = on
    el.classList.toggle('is-pressed', on)
}

function release(el: HTMLElement, s: PressState) {
    if (s.timer) clearTimeout(s.timer)
    s.timer = null
    s.pointerId = null
    s.cleanup?.()
    s.cleanup = null
    setPressed(el, s, false)
}

function onPointerDown(el: HTMLElement, s: PressState, e: PointerEvent) {
    if (isDisabled(el, s) || !e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return
    release(el, s)
    if (s.flash) clearTimeout(s.flash)
    s.flash = null
    s.pointerId = e.pointerId
    s.x = e.clientX
    s.y = e.clientY

    const touch = e.pointerType !== 'mouse'
    if (touch && inScroller(el)) {
        s.timer = setTimeout(() => {
            s.timer = null
            setPressed(el, s, true)
        }, PRESS_SCROLL_DELAY_MS)
    } else {
        setPressed(el, s, true)
    }

    const move = (ev: PointerEvent) => {
        if (ev.pointerId !== s.pointerId) return
        if (Math.hypot(ev.clientX - s.x, ev.clientY - s.y) > PRESS_SLOP_PX) release(el, s)
    }
    const up = (ev: PointerEvent) => {
        if (ev.pointerId !== s.pointerId) return
        const pending = s.timer !== null
        release(el, s)
        if (pending) {
            // A quick tap in a list: show the press briefly so the tap still feels acknowledged.
            setPressed(el, s, true)
            s.flash = setTimeout(() => {
                s.flash = null
                setPressed(el, s, false)
            }, TAP_FLASH_MS)
        }
    }
    const cancel = (ev: Event) => {
        if (ev instanceof PointerEvent && ev.pointerId !== s.pointerId) return
        release(el, s)
    }
    const leave = (ev: PointerEvent) => {
        if (ev.pointerType === 'mouse') release(el, s)
    }
    const scroll = () => release(el, s)
    window.addEventListener('pointermove', move, { passive: true })
    window.addEventListener('pointerup', up, { passive: true })
    window.addEventListener('pointercancel', cancel, { passive: true })
    window.addEventListener('scroll', scroll, { passive: true, capture: true })
    el.addEventListener('pointerleave', leave, { passive: true })
    s.cleanup = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        window.removeEventListener('pointercancel', cancel)
        window.removeEventListener('scroll', scroll, { capture: true })
        el.removeEventListener('pointerleave', leave)
    }
}

export const vPress: Directive<HTMLElement, PressValue> = {
    mounted(el, binding) {
        const s: PressState = {
            config: resolveConfig(binding),
            pressed: false,
            timer: null,
            flash: null,
            pointerId: null,
            x: 0,
            y: 0,
            cleanup: null,
            onDown: (e) => onPointerDown(el, s, e),
            onClick: () => {
                if (s.config.haptic && !isDisabled(el, s)) haptic(s.config.haptic)
            },
        }
        states.set(el, s)
        applyClasses(el, s)
        el.addEventListener('pointerdown', s.onDown, { passive: true })
        el.addEventListener('click', s.onClick)
    },
    updated(el, binding) {
        const s = states.get(el)
        if (!s) return
        s.config = resolveConfig(binding)
        if (s.config.disabled) release(el, s)
        applyClasses(el, s)
    },
    beforeUnmount(el) {
        const s = states.get(el)
        if (!s) return
        release(el, s)
        if (s.flash) clearTimeout(s.flash)
        el.removeEventListener('pointerdown', s.onDown)
        el.removeEventListener('click', s.onClick)
        states.delete(el)
    },
}

/** Registers `v-press` globally (main.ts and every dev entry call this). */
export function installPress(app: App): void {
    app.directive('press', vPress)
}

declare module 'vue' {
    interface GlobalDirectives {
        vPress: typeof vPress
    }
}
